import React from 'react'
import { useNavigate } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { toast } from 'react-hot-toast'
import {
  signInWithPopup,
  signInWithRedirect,
  OAuthCredential,
} from 'firebase/auth'

import { AppButton } from '@/components'
import { useSignInWithApple } from '@/domain/auth/useCases/useSignInWithApple'
import { useUserAuthSession } from '@/hooks'
import { useProfileDraftStore } from '@/store'
import { useAuthScopedStore } from '@/store/scoped'
// import { auth, appleProvider } from '@/config/firebase'

/**
 * Apple Sign In через Firebase Auth
 *
 * Аналогично Google Auth - используем Firebase для единообразия
 *
 * Flow:
 * 1. Firebase открывает Apple OAuth (popup или redirect)
 * 2. User авторизуется через Apple
 * 3. Firebase возвращает id_token
 * 4. Отправляем id_token на backend
 * 5. Backend валидирует и создает сессию
 */
export const ContinueWithApple: React.FC = () => {
  const { t } = useTranslation()
  const navigate = useNavigate()

  const setIdentificationMethod = useAuthScopedStore(
    (state) => state.setIdentificationMethod,
  )
  const { startSession } = useUserAuthSession()
  const setDraft = useProfileDraftStore((state) => state.setDraft)

  const { mutateAsync: signInWithApple } = useSignInWithApple({
    onSuccess: async ({ access_token, refresh_token, next_step }) => {
      await startSession({
        token: access_token,
        refreshToken: refresh_token,
      })

      if (next_step === 'SETUP_PROFILE') {
        navigate('/setup-profile')
      } else {
        navigate('/dashboard')
      }
    },
    onError: ({ errorMessage }) => {
      toast.error(errorMessage || 'Not mapped error')
    },
  })

  const processAppleAuthResult = React.useCallback(
    async (result: any) => {
      // Получаем Apple OAuth credential
      const credential = OAuthCredential.fromJSON(
        result._tokenResponse,
      ) as OAuthCredential

      if (!credential || !credential.idToken) {
        throw new Error('No Apple OAuth ID token found in response')
      }

      const idToken = credential.idToken

      // Получаем user data из Firebase
      const displayName = result.user.displayName || ''
      const email = result.user.email || ''

      // Apple часто возвращает имя в формате "FirstName LastName"
      const nameParts = displayName.split(' ')
      const firstName = nameParts[0] || ''
      const lastName = nameParts.slice(1).join(' ') || ''

      const profileData = {
        firstName,
        lastName,
        email,
        avatar: '', // Apple не возвращает фото
      }

      setDraft(profileData)
      setIdentificationMethod('apple')

      // Отправляем id_token на backend
      await signInWithApple({
        id_token: idToken,
      })
    },
    [signInWithApple, setDraft, setIdentificationMethod],
  )

  // Redirect result обрабатывается глобально в FirebaseAuthRedirectHandler
  // Локальная обработка убрана чтобы избежать двойной обработки

  const handleAppleAuth = async () => {
    try {
      setIdentificationMethod('apple')

      // Проверяем запущено ли в Telegram
      const isInTelegram = !!(window as any).Telegram?.WebApp

      // Пробуем popup (работает в обычном браузере)
      try {
        // const result = await signInWithPopup(auth, appleProvider)
        // await processAppleAuthResult(result)
      } catch (popupError: any) {

        // В Telegram НЕ используем redirect (открывает два окна)
        if (isInTelegram) {
          if (
            popupError.code === 'auth/popup-blocked' ||
            popupError.code === 'auth/popup-closed-by-user'
          ) {
            toast.error(
              'Please allow popups in Telegram settings to sign in with Apple',
            )
            return
          }
        }

        // Вне Telegram - используем redirect fallback
        if (
          popupError.code === 'auth/popup-blocked' ||
          popupError.code === 'auth/popup-closed-by-user' ||
          popupError.message.includes('Cross-Origin-Opener-Policy')
        ) {
          toast('Opening Apple authorization...', { duration: 2000 })
          // await signInWithRedirect(auth, appleProvider)
        } else {
          throw popupError
        }
      }
    } catch (error: any) {
      // Обработка специфичных ошибок
      let errorMessage = 'Failed to start Apple authentication'

      if (error.code === 'auth/operation-not-allowed') {
        errorMessage = 'Apple Sign In is not enabled in Firebase Console'
      } else if (error.code === 'auth/unauthorized-domain') {
        errorMessage = 'Domain is not authorized in Firebase Console'
      } else if (error.code === 'auth/cancelled-popup-request') {
        // User cancelled - не показываем ошибку
        return
      }

      toast.error(errorMessage)
    }
  }

  // Apple Sign In доступен везде (Firebase обрабатывает)
  return (
    <AppButton
      iconName="apple"
      iconGap={12}
      iconSize={24}
      isDefaultIconColor
      label={t('form:actions.continueWith', { authMethod: 'Apple' })}
      onPress={handleAppleAuth}
      type="secondaryGrayDark"
    />
  )
}
